/**
 * Component Registry for Gallery Residential Theme
 * 
 * Extends the base blue-haven theme registry with Gallery Residential specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-blue-haven/registry';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { AmenitiesFeatures } from './components/AmenitiesFeatures';
import { AmenitiesList } from './components/AmenitiesList';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { PropertyOverview } from './components/PropertyOverview';
import { DiscoverAmenities } from './components/DiscoverAmenities';
import { PinnedCallouts } from './components/PinnedCallouts';
import { HeroImageBG } from './components/HeroImageBG';
import { ContactMap } from './components/ContactMap';
import { ContactForm } from './components/ContactForm';
import { AmenitiesIntro } from './components/AmenitiesIntro';
import { Footer } from './components/Footer';
import { GalleryIntro } from './components/GalleryIntro';
import { GalleryPhotos } from './components/GalleryPhotos';
import { PageIntro } from './components/PageIntro';
import { ApplyNowOptions } from './components/ApplyNowOptions';
import { ResidentAccess } from './components/ResidentAccess';
import { NeighborhoodIntro } from './components/NeighborhoodIntro';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { ThankYouContent } from './components/ThankYouContent';
import { SitemapContent } from './components/SitemapContent';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { PetPolicy } from './components/PetPolicy';
import { InteractiveMapSection } from './components/InteractiveMapSection';
import { FloorPlansIntro } from './components/FloorPlansIntro';
import { VirtualTourIntro } from './components/VirtualTourIntro';
import { FAQIntro } from './components/FAQIntro';

/**
 * Gallery Residential specific component overrides.
 * These components have been customized for the Gallery Residential brand.
 * 
 * Example:
 * import { Footer } from './components/Footer';
 * 
 * const galleryresidentialOverrides: Partial<ComponentRegistry> = {
 *   Footer,
 * };
 */
const galleryresidentialOverrides: Partial<ComponentRegistry> = {
  Header,
  Hero,
  AmenitiesFeatures,
  AmenitiesList,
  SpecialOfferBanner,
  PropertyOverview,
  DiscoverAmenities,
  PinnedCallouts,
  HeroImageBG,
  ContactMap,
  ContactForm,
  AmenitiesIntro,
  Footer,
  GalleryIntro,
  GalleryPhotos,
  PageIntro,
  ApplyNowOptions,
  ResidentAccess,
  NeighborhoodIntro,
  AccessibilityStatement,
  ThankYouContent,
  SitemapContent,
  VirtualTourEmbed,
  PetPolicy,
  InteractiveMapSection,
  FloorPlansIntro,
  VirtualTourIntro,
  FAQIntro,
};

/**
 * Final merged registry for Gallery Residential properties.
 * Inherits all components from the blue-haven theme, with Gallery Residential overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...galleryresidentialOverrides,
};

export default registry;
